import React, { useState } from 'react';
import { X, Loader2, Send, AlertTriangle, CheckCircle, Users, FileText, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from './constants';
import { PitchEmailModal } from './PitchEmailModal';

export const SubmissionFlow = ({ roleId, roleTitle, company, location, candidateKeys, candidateNames, pitchText, pitchType, onClose, onSubmitted }) => {
  const [clientCompany, setClientCompany] = useState(company || '');
  const [editedPitch, setEditedPitch] = useState(pitchText || '');
  const [submitting, setSubmitting] = useState(false);
  const [showPitch, setShowPitch] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);

  const handleSubmit = async () => {
    if (!clientCompany.trim()) { toast.error('Client company is required'); return; }
    if (!candidateKeys.length) { toast.error('No candidates selected'); return; }
    if (!editedPitch.trim()) { toast.error('Pitch text is required'); return; }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          client_company: clientCompany.trim(),
          role_id: roleId,
          candidate_keys: candidateKeys,
          pitch_text: editedPitch.trim(),
          pitch_type: pitchType || 'short',
        }),
      });
      const data = await res.json();
      if (data.error) {
        if (data.duplicate_submission_id) {
          toast.error(data.detail, { duration: 6000 });
        } else {
          toast.error(data.detail);
        }
      } else {
        toast.success(`Submission ${data.submission_id} logged — ${data.candidates_submitted} candidates`);
        setSubmissionId(data.submission_id);
        if (onSubmitted) onSubmitted(data);
      }
    } catch { toast.error('Submission failed'); }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="submission-flow-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 bg-emerald-600 rounded-lg flex items-center justify-center">
              <Send className="h-4 w-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-[#0A1F44]">Submit to Client</h2>
              <p className="text-[10px] text-gray-500">{roleTitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="h-4 w-4 text-gray-400" /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Client Company */}
          <div>
            <label className="text-[10px] text-gray-500 uppercase tracking-wider font-medium block mb-1">Client / Company</label>
            <input type="text" value={clientCompany} onChange={e => setClientCompany(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50"
              placeholder="Enter client company name"
              data-testid="submission-client-input" />
          </div>

          {/* Role Confirmation */}
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-1">Role</p>
            <p className="text-sm font-semibold text-[#0A1F44]">{roleTitle}</p>
            {location && <p className="text-xs text-gray-500">{location}</p>}
          </div>

          {/* Candidates Confirmation */}
          <div>
            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-1.5">
              <Users className="h-3 w-3 inline mr-1" />Candidates ({candidateNames.length})
            </p>
            <div className="flex flex-wrap gap-1.5">
              {candidateNames.map((n, i) => (
                <span key={i} className="flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-lg border border-emerald-200">
                  <CheckCircle className="h-3 w-3" /> {n}
                </span>
              ))}
            </div>
          </div>

          {/* Pitch Preview */}
          <div>
            <button onClick={() => setShowPitch(s => !s)}
              className="flex items-center gap-1 text-[10px] text-gray-500 uppercase tracking-wider font-medium hover:text-[#0A1F44]">
              <FileText className="h-3 w-3" /> Pitch Preview {showPitch ? '(hide)' : '(show)'}
            </button>
            {showPitch && (
              <textarea value={editedPitch} onChange={e => setEditedPitch(e.target.value)}
                className="w-full mt-1.5 p-3 text-xs text-gray-700 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#D4AF37] min-h-[120px] resize-y"
                data-testid="submission-pitch-editor" />
            )}
          </div>

          {/* Warning */}
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-[10px] text-amber-700">This will log the submission and mark candidates as "Submitted". Make sure all details are correct.</p>
          </div>
        </div>

        {!submissionId ? (
          <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-end gap-2 bg-gray-50 rounded-b-xl">
            <button onClick={onClose} className="px-4 py-2 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100">Cancel</button>
            <button onClick={handleSubmit} disabled={submitting || !clientCompany.trim()}
              className="px-6 py-2 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="confirm-submit-btn">
              {submitting ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Submitting...</> : <><Send className="h-3.5 w-3.5" /> Confirm Submission</>}
            </button>
          </div>
        ) : (
          <div className="px-6 py-4 border-t border-gray-200 bg-emerald-50 rounded-b-xl">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
              <p className="text-xs font-bold text-emerald-700">Submission logged ({submissionId})</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowEmailModal(true)}
                disabled
                className="px-4 py-2 bg-gray-200 text-gray-400 text-xs font-bold rounded-lg cursor-not-allowed flex items-center gap-1.5"
                title="Email sending disabled — domain verification required"
                data-testid="send-pitch-email-btn">
                <Mail className="h-3.5 w-3.5" /> Send Pitch via Email
              </button>
              <span className="text-[9px] text-amber-600 font-medium">Domain verification pending</span>
              <button onClick={onClose}
                className="px-4 py-2 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-white"
                data-testid="close-after-submit">
                Done
              </button>
            </div>
          </div>
        )}

        {showEmailModal && (
          <PitchEmailModal
            roleId={roleId}
            roleTitle={roleTitle}
            company={clientCompany}
            candidateKeys={candidateKeys}
            candidateNames={candidateNames}
            pitchText={editedPitch}
            pitchType={pitchType}
            submissionId={submissionId}
            onClose={() => setShowEmailModal(false)}
            onSent={() => {
              setShowEmailModal(false);
              toast.success('Pitch emailed and submission updated');
              onClose();
            }}
          />
        )}
      </div>
    </div>
  );
};
